import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { etichettaImpresa, ordinaPerPratica } from '../../utils/pratica';

const SOA_LIST = ['SOATECH', 'SOA Mediterranea'];

const PLACEHOLDERS_PDF = [
  'ragione_sociale', 'partita_iva', 'codice_fiscale', 'indirizzo', 'citta', 'cap', 'provincia',
  'camera_commercio_di', 'iscrizione_cciaa', 'data_iscrizione_cciaa',
  'email', 'data_attestazione', 'soa', 'data_contratto',
  'data_oggi', 'anno_corrente',
  'lr_nome', 'lr_cognome', 'lr_nome_cognome', 'lr_cognome_nome',
  'lr_nato_a', 'lr_provincia_nascita', 'lr_nato_il', 'lr_codice_fiscale',
  'lr_residente_a', 'lr_provincia_residenza', 'lr_indirizzo_residenza',
  'dt_nome', 'dt_cognome', 'dt_nome_cognome', 'dt_cognome_nome', 'dt_titolo',
  'dt_nato_a', 'dt_provincia_nascita', 'dt_nato_il', 'dt_codice_fiscale',
  'dt_residente_a', 'dt_provincia_residenza', 'dt_indirizzo_residenza',
  'dt2_nome', 'dt2_cognome', 'dt2_nome_cognome', 'dt2_cognome_nome', 'dt2_titolo',
  'dt2_nato_a', 'dt2_provincia_nascita', 'dt2_nato_il', 'dt2_codice_fiscale',
  'dt2_residente_a', 'dt2_provincia_residenza', 'dt2_indirizzo_residenza',
  'dt3_nome', 'dt3_cognome', 'dt3_nome_cognome', 'dt3_cognome_nome', 'dt3_titolo',
  'dt3_nato_a', 'dt3_provincia_nascita', 'dt3_nato_il', 'dt3_codice_fiscale',
  'dt3_residente_a', 'dt3_provincia_residenza', 'dt3_indirizzo_residenza',
];

export default function Documenti() {
  const [tabSoa, setTabSoa] = useState('SOATECH');
  const [tipoDoc, setTipoDoc] = useState('word'); // 'word' | 'pdf' | 'excel'
  const [errore, setErrore] = useState('');
  const [successo, setSuccesso] = useState('');

  // ── Imprese / LR / DT (condivisi tra Word e PDF) ──────────────
  const [imprese, setImprese] = useState([]);
  const [impresaId, setImpresaId] = useState('');
  const [lrList, setLrList] = useState([]);
  const [dtList, setDtList] = useState([]);
  const [lrId, setLrId] = useState('');
  const [dtId, setDtId] = useState('');
  const [dt2Id, setDt2Id] = useState('');
  const [dt3Id, setDt3Id] = useState('');

  // ── WORD ───────────────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [nomeModello, setNomeModello] = useState('');
  const [fileModello, setFileModello] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [generando, setGenerando] = useState(false);
  const fileRef = useRef();

  // ── PDF ────────────────────────────────────────────────────────
  const [pdfTemplates, setPdfTemplates] = useState([]);
  const [pdfNome, setPdfNome] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfMapFile, setPdfMapFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfTemplateSel, setPdfTemplateSel] = useState(null);
  const [pdfFields, setPdfFields] = useState([]);
  const [pdfChanges, setPdfChanges] = useState({});
  const [pdfPagesOpen, setPdfPagesOpen] = useState({});
  const [pdfSaving, setPdfSaving] = useState(false);
  const [pdfGenerando, setPdfGenerando] = useState(false);
  const pdfFileRef = useRef();
  const pdfMapRef = useRef();

  // ── EXCEL ──────────────────────────────────────────────────────
  const [excelTemplates, setExcelTemplates] = useState([]);
  const [excelNome, setExcelNome] = useState('');
  const [excelFile, setExcelFile] = useState(null);
  const [excelUploading, setExcelUploading] = useState(false);
  const [excelTemplateId, setExcelTemplateId] = useState('');
  const [excelGenerando, setExcelGenerando] = useState(false);
  const [excelAnteprima, setExcelAnteprima] = useState(null);
  const excelFileRef = useRef();

  // ── Carica imprese ─────────────────────────────────────────────
  useEffect(() => {
    axios.get('/api/imprese').then(r => setImprese(r.data)).catch(() => {});
  }, []);

  // ── Carica LR/DT quando cambia impresa ────────────────────────
  useEffect(() => {
    if (!impresaId) {
      setLrList([]); setDtList([]);
      setLrId(''); setDtId(''); setDt2Id(''); setDt3Id('');
      return;
    }
    Promise.all([
      axios.get(`/api/imprese/${impresaId}/legali-rappresentanti`),
      axios.get(`/api/imprese/${impresaId}/direttori-tecnici`),
    ]).then(([lr, dt]) => {
      setLrList(lr.data);
      setDtList(dt.data);
      setLrId(lr.data.length === 1 ? String(lr.data[0].id) : '');
      setDtId(dt.data.length === 1 ? String(dt.data[0].id) : '');
      setDt2Id(''); setDt3Id('');
    }).catch(() => {});
  }, [impresaId]);

  // ── Carica Word templates quando cambia SOA ───────────────────
  useEffect(() => {
    axios.get('/api/documenti/templates', { params: { soa: tabSoa } })
      .then(r => setTemplates(r.data)).catch(() => {});
    setTemplateId('');
  }, [tabSoa]);

  // ── Carica PDF templates quando cambia SOA ────────────────────
  useEffect(() => {
    axios.get('/api/pdf-templates', { params: { soa: tabSoa } })
      .then(r => setPdfTemplates(r.data)).catch(() => {});
    setPdfTemplateSel(null); setPdfFields([]); setPdfChanges({});
  }, [tabSoa]);

  // ── Carica modelli Excel quando cambia SOA ────────────────────
  useEffect(() => {
    axios.get('/api/excel-templates', { params: { soa: tabSoa } })
      .then(r => setExcelTemplates(r.data)).catch(() => {});
    setExcelTemplateId(''); setExcelAnteprima(null);
  }, [tabSoa]);

  // ── Anteprima dei dati che finiranno nel foglio ───────────────
  useEffect(() => {
    if (!excelTemplateId || !impresaId) { setExcelAnteprima(null); return; }
    axios.get(`/api/excel-templates/${excelTemplateId}/anteprima/${impresaId}`)
      .then(r => setExcelAnteprima(r.data)).catch(() => setExcelAnteprima(null));
  }, [excelTemplateId, impresaId]);

  // ── WORD: upload ───────────────────────────────────────────────
  async function uploadWord(e) {
    e.preventDefault();
    if (!nomeModello || !fileModello) return;
    setUploading(true);
    setErrore('');
    try {
      const fd = new FormData();
      fd.append('nome', nomeModello);
      fd.append('soa', tabSoa);
      fd.append('file', fileModello);
      await axios.post('/api/documenti/templates', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNomeModello(''); setFileModello(null);
      if (fileRef.current) fileRef.current.value = '';
      const r = await axios.get('/api/documenti/templates', { params: { soa: tabSoa } });
      setTemplates(r.data);
    } catch { setErrore('Errore nel caricamento del modello'); }
    finally { setUploading(false); }
  }

  async function scaricaOriginale(url, fileNome) {
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = href;
      a.download = fileNome;
      a.click();
      URL.revokeObjectURL(href);
    } catch { setErrore('Errore nel download del modello'); }
  }

  async function eliminaWord(id) {
    if (!window.confirm('Eliminare questo modello?')) return;
    try {
      await axios.delete(`/api/documenti/templates/${id}`);
      setTemplates(t => t.filter(x => x.id !== id));
      if (templateId === String(id)) setTemplateId('');
    } catch { setErrore('Errore nell\'eliminazione'); }
  }

  async function generaWord(e) {
    e.preventDefault();
    if (!templateId || !impresaId) return;
    setGenerando(true); setErrore('');
    try {
      const res = await axios.post('/api/documenti/genera',
        { template_id: parseInt(templateId), impresa_id: parseInt(impresaId),
          lr_id: lrId ? parseInt(lrId) : null,
          dt_id: dtId ? parseInt(dtId) : null,
          dt2_id: dt2Id ? parseInt(dt2Id) : null,
          dt3_id: dt3Id ? parseInt(dt3Id) : null },
        { responseType: 'blob' }
      );
      const t = templates.find(t => String(t.id) === templateId);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = t?.file_nome || 'documento.docx'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'Errore nella generazione.';
      try {
        // responseType blob: leggi il JSON di errore dal blob
        const text = await err.response?.data?.text?.();
        const json = JSON.parse(text || '{}');
        if (json.error) msg = json.error;
      } catch {}
      setErrore(msg);
    }
    finally { setGenerando(false); }
  }

  // ── PDF: upload ────────────────────────────────────────────────
  async function uploadPdf(e) {
    e.preventDefault();
    if (!pdfNome || !pdfFile) return;
    setPdfUploading(true); setErrore('');
    try {
      const fd = new FormData();
      fd.append('nome', pdfNome);
      fd.append('soa', tabSoa);
      fd.append('pdf', pdfFile);
      if (pdfMapFile) fd.append('field_map', pdfMapFile);
      const r = await axios.post('/api/pdf-templates', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccesso(`Template "${r.data.nome}" caricato con ${r.data.campi} campi`);
      setPdfNome(''); setPdfFile(null); setPdfMapFile(null);
      if (pdfFileRef.current) pdfFileRef.current.value = '';
      if (pdfMapRef.current) pdfMapRef.current.value = '';
      const ts = await axios.get('/api/pdf-templates', { params: { soa: tabSoa } });
      setPdfTemplates(ts.data);
    } catch (err) { setErrore(err.response?.data?.error || 'Errore nel caricamento'); }
    finally { setPdfUploading(false); }
  }

  async function eliminaPdf(id) {
    if (!window.confirm('Eliminare questo template PDF?')) return;
    try {
      await axios.delete(`/api/pdf-templates/${id}`);
      setPdfTemplates(t => t.filter(x => x.id !== id));
      if (pdfTemplateSel?.id === id) { setPdfTemplateSel(null); setPdfFields([]); setPdfChanges({}); }
    } catch { setErrore('Errore nell\'eliminazione'); }
  }

  async function selezionaPdfTemplate(t) {
    setPdfTemplateSel(t); setPdfChanges({});
    try {
      const r = await axios.get(`/api/pdf-templates/${t.id}/fields`);
      setPdfFields(r.data);
      const pages = [...new Set(r.data.map(f => f.page))];
      if (pages.length > 0) setPdfPagesOpen({ [pages[0]]: true });
    } catch { setErrore('Errore nel caricamento dei campi'); }
  }

  function getPdfVal(field, key) {
    return pdfChanges[field.id]?.[key] !== undefined ? pdfChanges[field.id][key] : (field[key] || '');
  }

  function aggiornaPdfCampo(id, key, value) {
    setPdfChanges(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));
  }

  async function salvaMappe() {
    if (!pdfTemplateSel) return;
    setPdfSaving(true); setErrore('');
    try {
      const payload = pdfFields.map(f => ({
        id: f.id,
        placeholder: getPdfVal(f, 'placeholder'),
        fixed_value: getPdfVal(f, 'fixed_value'),
      }));
      await axios.put(`/api/pdf-templates/${pdfTemplateSel.id}/fields`, { fields: payload });
      setPdfFields(prev => prev.map(f => ({
        ...f,
        placeholder: getPdfVal(f, 'placeholder') || null,
        fixed_value: getPdfVal(f, 'fixed_value') || null,
      })));
      setPdfChanges({});
      setSuccesso('Mappature salvate');
    } catch (err) { setErrore(err.response?.data?.error || err.message || 'Errore nel salvataggio'); }
    finally { setPdfSaving(false); }
  }

  async function generaPdf(e) {
    e.preventDefault();
    if (!pdfTemplateSel || !impresaId) return;
    setPdfGenerando(true); setErrore('');
    try {
      const res = await axios.post(
        `/api/pdf-templates/${pdfTemplateSel.id}/genera/${impresaId}`,
        { lr_id: lrId || null, dt_id: dtId || null, dt2_id: dt2Id || null, dt3_id: dt3Id || null },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${pdfTemplateSel.nome.replace(/\s+/g, '_')}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { setErrore(err.response?.data?.error || err.message || 'Errore nella generazione del PDF'); }
    finally { setPdfGenerando(false); }
  }

  // ── EXCEL: upload / elimina / genera ──────────────────────────
  async function uploadExcel(e) {
    e.preventDefault();
    if (!excelNome || !excelFile) return;
    setExcelUploading(true); setErrore('');
    try {
      const fd = new FormData();
      fd.append('nome', excelNome);
      fd.append('soa', tabSoa);
      fd.append('file', excelFile);
      await axios.post('/api/excel-templates', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setExcelNome(''); setExcelFile(null);
      if (excelFileRef.current) excelFileRef.current.value = '';
      const r = await axios.get('/api/excel-templates', { params: { soa: tabSoa } });
      setExcelTemplates(r.data);
      setSuccesso('Modello Excel caricato');
    } catch (err) { setErrore(err.response?.data?.error || 'Errore nel caricamento del modello'); }
    finally { setExcelUploading(false); }
  }

  async function eliminaExcel(id) {
    if (!window.confirm('Eliminare questo modello Excel?')) return;
    try {
      await axios.delete(`/api/excel-templates/${id}`);
      setExcelTemplates(t => t.filter(x => x.id !== id));
      if (excelTemplateId === String(id)) { setExcelTemplateId(''); setExcelAnteprima(null); }
    } catch { setErrore('Errore nell\'eliminazione'); }
  }

  async function generaExcel(e) {
    e.preventDefault();
    if (!excelTemplateId || !impresaId) return;
    setExcelGenerando(true); setErrore('');
    try {
      const res = await axios.post('/api/excel-templates/genera',
        { template_id: parseInt(excelTemplateId), impresa_id: parseInt(impresaId),
          lr_id: lrId ? parseInt(lrId) : null },
        { responseType: 'blob' }
      );
      const t = excelTemplates.find(t => String(t.id) === excelTemplateId);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t?.nome || 'modello'} - ${impresaSel?.ragione_sociale || ''}.xlsx`.replace(/[/\\?%*:|"<>]/g, '-');
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'Errore nella generazione.';
      try {
        const text = await err.response?.data?.text?.();
        const json = JSON.parse(text || '{}');
        if (json.error) msg = json.error;
      } catch {}
      setErrore(msg);
    }
    finally { setExcelGenerando(false); }
  }

  const impresaSel = imprese.find(i => String(i.id) === impresaId);
  const pdfPages = [...new Set(pdfFields.map(f => f.page))].sort((a, b) => a - b);
  const pdfHasChanges = Object.keys(pdfChanges).length > 0;

  const selectCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

  // LR/DT selects condivisi
  function LrDtSelects() {
    return (
      <>
        {lrList.length > 0 && (
          <div>
            <label className={labelCls}>Legale Rappresentante</label>
            <select value={lrId} onChange={e => setLrId(e.target.value)} className={selectCls}>
              <option value="">— Nessuno —</option>
              {lrList.map(lr => <option key={lr.id} value={lr.id}>{lr.cognome} {lr.nome}</option>)}
            </select>
          </div>
        )}
        {dtList.length > 0 && (
          <div>
            <label className={labelCls}>Direttore Tecnico 1</label>
            <select value={dtId} onChange={e => setDtId(e.target.value)} className={selectCls}>
              <option value="">— Nessuno —</option>
              {dtList.map(dt => <option key={dt.id} value={dt.id}>{dt.cognome} {dt.nome}{dt.titolo ? ` (${dt.titolo})` : ''}</option>)}
            </select>
          </div>
        )}
        {dtList.length > 1 && (
          <div>
            <label className={labelCls}>Direttore Tecnico 2</label>
            <select value={dt2Id} onChange={e => setDt2Id(e.target.value)} className={selectCls}>
              <option value="">— Nessuno —</option>
              {dtList.map(dt => <option key={dt.id} value={dt.id}>{dt.cognome} {dt.nome}{dt.titolo ? ` (${dt.titolo})` : ''}</option>)}
            </select>
          </div>
        )}
        {dtList.length > 2 && (
          <div>
            <label className={labelCls}>Direttore Tecnico 3</label>
            <select value={dt3Id} onChange={e => setDt3Id(e.target.value)} className={selectCls}>
              <option value="">— Nessuno —</option>
              {dtList.map(dt => <option key={dt.id} value={dt.id}>{dt.cognome} {dt.nome}{dt.titolo ? ` (${dt.titolo})` : ''}</option>)}
            </select>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-[#1A3A5C] mb-6">Documenti e Dichiarazioni</h1>

      {/* Tab SOA */}
      <div className="flex gap-2 mb-4 border-b border-gray-200">
        {SOA_LIST.map(s => (
          <button key={s} onClick={() => setTabSoa(s)}
            className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition ${
              tabSoa === s ? 'border-[#2E6096] text-[#2E6096] bg-[#EEF4FB]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {s}
          </button>
        ))}
      </div>

      {/* Toggle Word / PDF */}
      <div className="flex gap-2 mb-5">
        {[['word', 'Word (.docx)'], ['pdf', 'Moduli PDF'], ['excel', 'Excel (.xlsx)']].map(([tipo, etichetta]) => (
          <button key={tipo} onClick={() => setTipoDoc(tipo)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition border ${
              tipoDoc === tipo
                ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]'
                : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'
            }`}>
            {etichetta}
          </button>
        ))}
      </div>

      {/* Messaggi */}
      {errore && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm flex justify-between">
          <span>{errore}</span>
          <button onClick={() => setErrore('')} className="text-red-400 hover:text-red-600 ml-3">✕</button>
        </div>
      )}
      {successo && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 mb-4 text-sm flex justify-between">
          <span>{successo}</span>
          <button onClick={() => setSuccesso('')} className="text-green-400 hover:text-green-600 ml-3">✕</button>
        </div>
      )}

      {/* ══════════════════ WORD ══════════════════ */}
      {tipoDoc === 'word' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Gestione modelli Word */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-bold text-[#1A3A5C] uppercase tracking-wide mb-4">
              Modelli {tabSoa}
            </h2>
            <form onSubmit={uploadWord} className="mb-4 space-y-2">
              <input value={nomeModello} onChange={e => setNomeModello(e.target.value)}
                placeholder="Nome modello (es. Dichiarazione antimafia)"
                className={selectCls} required />
              <input ref={fileRef} type="file" accept=".docx"
                onChange={e => setFileModello(e.target.files[0])}
                className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-[#EEF4FB] file:text-[#2E6096] hover:file:bg-[#dbeafe]"
                required />
              <button type="submit" disabled={uploading}
                className="w-full bg-[#2E6096] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#1A3A5C] disabled:opacity-50 transition">
                {uploading ? 'Caricamento…' : '+ Carica modello'}
              </button>
            </form>
            {templates.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nessun modello caricato</p>
            ) : (
              <ul className="space-y-2">
                {templates.map(t => (
                  <li key={t.id} onClick={() => setTemplateId(String(t.id))}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border cursor-pointer transition ${
                      templateId === String(t.id) ? 'border-[#2E6096] bg-[#EEF4FB]' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}>
                    <div>
                      <div className="text-sm font-medium text-gray-800">{t.nome}</div>
                      <div className="text-xs text-gray-400">{t.file_nome}</div>
                    </div>
                    <div className="flex items-center shrink-0">
                      <button onClick={e => { e.stopPropagation(); scaricaOriginale(`/api/documenti/templates/${t.id}/download`, t.file_nome); }}
                        className="text-[#2E6096] hover:text-[#1A3A5C] text-xs px-2 py-1 rounded hover:bg-[#EEF4FB] transition">
                        Scarica
                      </button>
                      <button onClick={e => { e.stopPropagation(); eliminaWord(t.id); }}
                        className="text-red-400 hover:text-red-600 text-xs px-2 py-1 rounded hover:bg-red-50 transition">
                        Elimina
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Genera Word */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-bold text-[#1A3A5C] uppercase tracking-wide mb-4">Genera documento</h2>
            <form onSubmit={generaWord} className="space-y-4">
              <div>
                <label className={labelCls}>Modello</label>
                <select value={templateId} onChange={e => setTemplateId(e.target.value)} className={selectCls} required>
                  <option value="">— Seleziona modello —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Impresa</label>
                <select value={impresaId} onChange={e => setImpresaId(e.target.value)} className={selectCls} required>
                  <option value="">— Seleziona impresa —</option>
                  {ordinaPerPratica(imprese).map(i => <option key={i.id} value={i.id}>{etichettaImpresa(i)}</option>)}
                </select>
                {impresaSel?.soa && <p className="text-xs text-gray-400 mt-1">SOA: {impresaSel.soa}</p>}
              </div>
              <LrDtSelects />
              {impresaId && lrList.length === 0 && dtList.length === 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded px-3 py-2">Nessun LR o DT per questa impresa.</p>
              )}
              <button type="submit" disabled={generando || !templateId || !impresaId}
                className="w-full bg-green-600 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition">
                {generando ? 'Generazione…' : '⬇ Scarica documento compilato'}
              </button>
            </form>
            <details className="mt-5">
              <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none">
                Elenco segnaposto disponibili
              </summary>
              <div className="mt-2 text-xs text-gray-500 space-y-1 leading-relaxed bg-gray-50 rounded p-3 font-mono">
                <div className="font-semibold text-gray-600 not-italic mb-1">Impresa</div>
                <div>{'{ragione_sociale}'} {'{partita_iva}'} {'{codice_fiscale}'}</div>
                <div>{'{indirizzo}'} {'{citta}'} {'{cap}'} {'{provincia}'}</div>
                <div>{'{camera_commercio_di}'} {'{iscrizione_cciaa}'} {'{data_iscrizione_cciaa}'}</div>
                <div>{'{data_contratto}'} {'{data_attestazione}'} {'{soa}'}</div>
                <div>{'{email}'} {'{data_oggi}'} {'{anno_corrente}'}</div>
                <div className="font-semibold text-gray-600 not-italic mt-2 mb-1">LR / DT</div>
                <div>{'{lr_nome}'} {'{lr_cognome}'} {'{lr_nome_cognome}'} {'{lr_nato_il}'} {'{lr_codice_fiscale}'}</div>
                <div>{'{dt_nome}'} {'{dt_cognome}'} {'{dt_nome_cognome}'} {'{dt_titolo}'}</div>
                <div>{'{dt2_nome}'} … {'{dt3_nome}'} …</div>
                <div className="font-semibold text-gray-600 not-italic mt-2 mb-1">Dati economici</div>
                <div>{'{anno1}'} … {'{va_anno1}'} … {'{patrimonio}'} {'{capitale_netto}'} {'{patrimonio_netto}'}</div>
                <div>{'{amm_attrezzature_anno1}'} … {'{leasing_anno1}'} … {'{amm_totale_generale}'}</div>
                <div>{'{cp_operai_anno1}'} … {'{cp_totale_generale}'} {'{cp_media}'}</div>
              </div>
            </details>
          </div>
        </div>
      )}

      {/* ══════════════════ PDF ══════════════════ */}
      {tipoDoc === 'pdf' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Gestione modelli PDF */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-bold text-[#1A3A5C] uppercase tracking-wide mb-4">
                Moduli PDF — {tabSoa}
              </h2>
              <form onSubmit={uploadPdf} className="mb-4 space-y-2">
                <input value={pdfNome} onChange={e => setPdfNome(e.target.value)}
                  placeholder="Nome modulo (es. Modulo A SOA)"
                  className={selectCls} required />
                <div>
                  <label className="block text-xs text-gray-500 mb-1">File PDF</label>
                  <input ref={pdfFileRef} type="file" accept=".pdf"
                    onChange={e => setPdfFile(e.target.files[0])}
                    className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-[#EEF4FB] file:text-[#2E6096]"
                    required />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    field_map.json <span className="text-gray-400">(opzionale)</span>
                  </label>
                  <input ref={pdfMapRef} type="file" accept=".json"
                    onChange={e => setPdfMapFile(e.target.files[0])}
                    className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-[#EEF4FB] file:text-[#2E6096]" />
                </div>
                <button type="submit" disabled={pdfUploading}
                  className="w-full bg-[#2E6096] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#1A3A5C] disabled:opacity-50 transition">
                  {pdfUploading ? 'Caricamento…' : '+ Carica modulo PDF'}
                </button>
              </form>
              {pdfTemplates.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Nessun modulo PDF caricato</p>
              ) : (
                <ul className="space-y-2">
                  {pdfTemplates.map(t => (
                    <li key={t.id} onClick={() => selezionaPdfTemplate(t)}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg border cursor-pointer transition ${
                        pdfTemplateSel?.id === t.id ? 'border-[#2E6096] bg-[#EEF4FB]' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{t.nome}</div>
                        <div className="text-xs text-gray-400 truncate">{t.file_nome}</div>
                      </div>
                      <div className="ml-2 flex items-center shrink-0">
                        <button onClick={e => { e.stopPropagation(); scaricaOriginale(`/api/pdf-templates/${t.id}/download`, t.file_nome); }}
                          className="text-[#2E6096] hover:text-[#1A3A5C] text-xs px-2 py-1 rounded hover:bg-[#EEF4FB] transition">
                          Scarica
                        </button>
                        <button onClick={e => { e.stopPropagation(); eliminaPdf(t.id); }}
                          className="text-red-400 hover:text-red-600 text-xs px-2 py-1 rounded hover:bg-red-50 transition">
                          Elimina
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Genera PDF */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-bold text-[#1A3A5C] uppercase tracking-wide mb-4">Genera PDF compilato</h2>
              {!pdfTemplateSel ? (
                <p className="text-sm text-gray-400 text-center py-4">Seleziona un modulo a sinistra</p>
              ) : (
                <form onSubmit={generaPdf} className="space-y-3">
                  <div className="text-xs font-medium text-[#2E6096] bg-[#EEF4FB] rounded px-2 py-1 truncate">
                    {pdfTemplateSel.nome}
                  </div>
                  <div>
                    <label className={labelCls}>Impresa</label>
                    <select value={impresaId} onChange={e => setImpresaId(e.target.value)} className={selectCls} required>
                      <option value="">— Seleziona —</option>
                      {ordinaPerPratica(imprese).map(i => <option key={i.id} value={i.id}>{etichettaImpresa(i)}</option>)}
                    </select>
                  </div>
                  <LrDtSelects />
                  <button type="submit" disabled={pdfGenerando || !impresaId}
                    className="w-full bg-green-600 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition">
                    {pdfGenerando ? 'Generazione…' : '⬇ Scarica PDF compilato'}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Mappatura campi PDF */}
          {pdfTemplateSel && pdfFields.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-[#1A3A5C] uppercase tracking-wide">
                  Mappatura campi — {pdfTemplateSel.nome}
                </h2>
                <button onClick={salvaMappe} disabled={pdfSaving || !pdfHasChanges}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
                    pdfHasChanges ? 'bg-[#2E6096] text-white hover:bg-[#1A3A5C]' : 'bg-gray-100 text-gray-400 cursor-default'
                  } disabled:opacity-50`}>
                  {pdfSaving ? 'Salvataggio…' : 'Salva mappature'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Per ogni campo indica il <strong>placeholder</strong> del gestionale (es. <code>ragione_sociale</code>)
                oppure un <strong>valore fisso</strong>. Per i checkbox: <code>Yes</code> = spuntato, lascia vuoto = non impostare.
              </p>

              <datalist id="ph-list">
                {PLACEHOLDERS_PDF.map(p => <option key={p} value={p} />)}
              </datalist>

              {pdfPages.map(pg => {
                const pgFields = pdfFields.filter(f => f.page === pg);
                const open = !!pdfPagesOpen[pg];
                const mappati = pgFields.filter(f => getPdfVal(f, 'placeholder') || getPdfVal(f, 'fixed_value')).length;
                return (
                  <div key={pg} className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setPdfPagesOpen(prev => ({ ...prev, [pg]: !prev[pg] }))}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700 transition">
                      <span>Pagina {pg} — {pgFields.length} campi</span>
                      <span className="flex items-center gap-3">
                        <span className="text-xs text-gray-400">{mappati}/{pgFields.length} mappati</span>
                        <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
                      </span>
                    </button>
                    {open && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="text-left px-3 py-2 font-semibold text-gray-600 w-36">Campo PDF</th>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600 w-20">Tipo</th>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600">Etichetta</th>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600 w-52">Placeholder gestionale</th>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600 w-36">Valore fisso</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pgFields.map(f => {
                              const ph = getPdfVal(f, 'placeholder');
                              const fv = getPdfVal(f, 'fixed_value');
                              return (
                                <tr key={f.id} className={`border-b border-gray-100 ${ph || fv ? 'bg-green-50' : ''}`}>
                                  <td className="px-3 py-2 font-mono text-gray-700">{f.field_name}</td>
                                  <td className="px-3 py-2">
                                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                      f.field_type === 'CheckBox' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                    }`}>{f.field_type}</span>
                                  </td>
                                  <td className="px-3 py-2 text-gray-400 italic truncate max-w-xs" title={f.label_guess || ''}>
                                    {f.label_guess || '—'}
                                  </td>
                                  <td className="px-3 py-2">
                                    {f.field_type === 'Text' && (
                                      <input list="ph-list" value={ph}
                                        onChange={e => aggiornaPdfCampo(f.id, 'placeholder', e.target.value)}
                                        placeholder="es. ragione_sociale"
                                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#2E6096]" />
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {f.field_type === 'CheckBox' ? (
                                      <select value={fv}
                                        onChange={e => aggiornaPdfCampo(f.id, 'fixed_value', e.target.value)}
                                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#2E6096]">
                                        <option value="">Non impostare</option>
                                        <option value="Yes">Spuntato</option>
                                        <option value="Off">Non spuntato</option>
                                      </select>
                                    ) : (
                                      <input value={fv}
                                        onChange={e => aggiornaPdfCampo(f.id, 'fixed_value', e.target.value)}
                                        placeholder="testo fisso"
                                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#2E6096]" />
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ══════════════════ EXCEL ══════════════════ */}
      {tipoDoc === 'excel' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Gestione modelli Excel */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-bold text-[#1A3A5C] uppercase tracking-wide mb-4">
              Modelli Excel — {tabSoa}
            </h2>
            <form onSubmit={uploadExcel} className="mb-4 space-y-2">
              <input value={excelNome} onChange={e => setExcelNome(e.target.value)}
                placeholder="Nome modello (es. Dichiarazioni D)"
                className={selectCls} required />
              <input ref={excelFileRef} type="file" accept=".xlsx,.xls"
                onChange={e => setExcelFile(e.target.files[0])}
                className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-[#EEF4FB] file:text-[#2E6096] hover:file:bg-[#dbeafe]"
                required />
              <button type="submit" disabled={excelUploading}
                className="w-full bg-[#2E6096] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#1A3A5C] disabled:opacity-50 transition">
                {excelUploading ? 'Caricamento…' : '+ Carica modello'}
              </button>
            </form>
            {excelTemplates.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nessun modello caricato</p>
            ) : (
              <ul className="space-y-2">
                {excelTemplates.map(t => (
                  <li key={t.id} onClick={() => setExcelTemplateId(String(t.id))}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border cursor-pointer transition ${
                      excelTemplateId === String(t.id) ? 'border-[#2E6096] bg-[#EEF4FB]' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}>
                    <div>
                      <div className="text-sm font-medium text-gray-800">{t.nome}</div>
                      <div className="text-xs text-gray-400">{t.file_nome}</div>
                    </div>
                    <div className="flex items-center shrink-0">
                      <button onClick={e => { e.stopPropagation(); scaricaOriginale(`/api/excel-templates/${t.id}/download`, t.file_nome); }}
                        className="text-[#2E6096] hover:text-[#1A3A5C] text-xs px-2 py-1 rounded hover:bg-[#EEF4FB] transition">
                        Scarica
                      </button>
                      <button onClick={e => { e.stopPropagation(); eliminaExcel(t.id); }}
                        className="text-red-400 hover:text-red-600 text-xs px-2 py-1 rounded hover:bg-red-50 transition">
                        Elimina
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-400 mt-4 leading-relaxed">
              Nel fascicolo delle dichiarazioni viene compilato il foglio <em>Dati anagrafici</em>:
              gli altri fogli si riempiono da soli tramite le formule già presenti nel file.
              In qualsiasi altro modello vengono sostituiti i segnaposto tipo <code className="bg-gray-100 px-1 rounded">{'{ragione_sociale}'}</code>.
            </p>
          </div>

          {/* Genera Excel */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-bold text-[#1A3A5C] uppercase tracking-wide mb-4">Genera file compilato</h2>
            <form onSubmit={generaExcel} className="space-y-4">
              <div>
                <label className={labelCls}>Modello</label>
                <select value={excelTemplateId} onChange={e => setExcelTemplateId(e.target.value)} className={selectCls} required>
                  <option value="">— Seleziona modello —</option>
                  {excelTemplates.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Impresa</label>
                <select value={impresaId} onChange={e => setImpresaId(e.target.value)} className={selectCls} required>
                  <option value="">— Seleziona impresa —</option>
                  {ordinaPerPratica(imprese).map(i => <option key={i.id} value={i.id}>{etichettaImpresa(i)}</option>)}
                </select>
                {impresaSel?.soa && <p className="text-xs text-gray-400 mt-1">SOA: {impresaSel.soa}</p>}
              </div>
              <LrDtSelects />
              {impresaId && lrList.length === 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded px-3 py-2">
                  Nessun legale rappresentante per questa impresa: i suoi dati resteranno vuoti.
                </p>
              )}
              <button type="submit" disabled={excelGenerando || !excelTemplateId || !impresaId}
                className="w-full bg-green-600 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition">
                {excelGenerando ? 'Generazione…' : '⬇ Scarica file compilato'}
              </button>
            </form>

            {excelAnteprima && (
              <details className="mt-5" open>
                <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none">
                  Anteprima dei dati che verranno inseriti
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {Object.entries(excelAnteprima.celle)
                        .filter(([cella]) => !cella.startsWith('F') || excelAnteprima.celle[cella])
                        .map(([cella, valore]) => (
                          <tr key={cella} className="border-b border-gray-100">
                            <td className="px-2 py-1 font-mono text-gray-400 w-12">{cella}</td>
                            <td className={`px-2 py-1 ${valore ? 'text-gray-700' : 'text-amber-600 italic'}`}>
                              {valore || 'dato mancante'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
